import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trophy, Plus, X, CalendarDays, Pencil, Trash2, Sparkles, Lock, Info, ChevronDown } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Field, GIORNI_SETTIMANA, toISO, fmtData, ConfirmAction } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

const MEDAL_COLORS = [
  { bg: "from-amber-400 to-yellow-500", ring: "ring-amber-300" },   // oro
  { bg: "from-slate-300 to-slate-400", ring: "ring-slate-300" },     // argento
  { bg: "from-orange-400 to-orange-600", ring: "ring-orange-300" },  // bronzo
];

const ordinalIt = (n) => `${n}°`;

/** Editor visuale della scala punti-per-posizione (podio + posizioni successive). */
function ScalaEditor({ scala, onChange }) {
  const set = (i, val) => {
    const v = parseInt(val, 10);
    const next = [...scala];
    next[i] = isNaN(v) ? 0 : v;
    onChange(next);
  };
  const add = () => onChange([...scala, Math.max(1, (scala[scala.length - 1] || 2) - 1)]);
  const remove = (i) => onChange(scala.filter((_, idx) => idx !== i));

  return (
    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
      {scala.map((punti, i) => {
        const medal = i < 3 ? MEDAL_COLORS[i] : null;
        return (
          <div key={i} className={`relative rounded-xl px-1.5 py-1 border transition-all ${medal ? "border-transparent" : "bg-slate-50 border-slate-200"}`}
            data-testid={`scala-card-${i}`}>
            {medal && <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${medal.bg} opacity-25`} />}
            <div className="relative flex items-center justify-between">
              <span className={`text-[11px] font-extrabold ${medal ? "text-slate-700" : "text-slate-400"}`}>{ordinalIt(i + 1)}</span>
              {scala.length > 1 && (
                <button type="button" onClick={() => remove(i)} className="text-slate-300 hover:text-red-500 transition-colors -mr-0.5" data-testid={`scala-remove-${i}`} aria-label="Rimuovi posizione">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <input type="number" min="0"
              className="relative w-full bg-transparent font-display font-extrabold text-lg leading-tight text-slate-900 focus:outline-none text-center"
              value={punti} onChange={(e) => set(i, e.target.value)} data-testid={`scala-punti-${i}`} />
          </div>
        );
      })}
      <button type="button" onClick={add}
        className="rounded-xl border border-dashed border-slate-300 px-1.5 py-1 flex flex-col items-center justify-center gap-0.5 text-slate-400 hover:text-amber-500 hover:border-amber-400 transition-colors min-h-[52px]"
        data-testid="scala-add-btn">
        <Plus className="h-4 w-4" />
        <span className="text-[10px] font-bold leading-none">Aggiungi</span>
      </button>
    </div>
  );
}

/** Pillole compatte che riassumono una scala (1° 30 · 2° 20 · …). */
function ScalaPills({ scala }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {scala.map((p, i) => (
        <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${i < 3 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
          <span className="opacity-60">{i + 1}°</span> {p}
        </span>
      ))}
    </div>
  );
}

/**
 * Nota esplicativa: la classifica non memorizza i punti, li ricalcola. Senza
 * questa premessa le scale sembrano non funzionare (o funzionare troppo).
 */
function ComeFunziona() {
  const [aperto, setAperto] = useState(false);
  return (
    <div className="card-app p-4 sm:p-6 mb-4" data-testid="come-funziona-punti">
      <button type="button" onClick={() => setAperto((v) => !v)}
        className="w-full flex items-center gap-2 text-left" aria-expanded={aperto} data-testid="come-funziona-toggle">
        <Info className="h-4 w-4 text-sky-500 shrink-0" />
        <p className="font-display font-extrabold text-slate-900 text-sm">Come vengono assegnati i punti</p>
        <ChevronDown className={`h-4 w-4 text-slate-400 ml-auto shrink-0 transition-transform ${aperto ? "rotate-180" : ""}`} />
      </button>

      {aperto && (
        <div className="mt-3 space-y-3 text-xs font-semibold text-slate-600 leading-relaxed">
          <p>
            <b className="text-slate-800">I punti non si scrivono: si calcolano.</b> Nelle Postazioni si registra
            solo il punteggio grezzo di ogni squadra (gol, secondi, birilli…). La classifica rifà il conto da capo
            ogni volta che la si apre, partendo da quei punteggi.
          </p>
          <p>
            <b className="text-slate-800">Una partita entra in classifica solo quando è completa</b>, cioè quando
            tutte le squadre hanno un risultato per quella postazione in quella giornata. Finché ne manca una,
            quella partita non assegna punti a nessuno.
          </p>
          <p>
            <b className="text-slate-800">Dal punteggio alla posizione.</b> Le squadre vengono ordinate dal punteggio
            grezzo più alto al più basso e ricevono i punti della posizione raggiunta. In caso di parità prendono
            gli stessi punti e la posizione successiva slitta: due prime a pari merito, poi la terza prende i punti
            del 3° posto. Chi arriva oltre l'ultima posizione della scala prende 0.
          </p>
          <div className="rounded-2xl bg-slate-50 p-3">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Quale scala si usa</p>
            <p>
              1. la <b>giornata speciale</b>, se quel giorno ne ha una · 2. la <b>scala del gioco</b>, se ne ha una
              propria · 3. altrimenti la <b>scala di default</b>.
            </p>
          </div>
          <p>
            <b className="text-slate-800">Bonus e malus</b> assegnati dalla sezione Squadre si sommano solo alla
            classifica generale: le classifiche per categoria restano quelle dei soli giochi.
          </p>
          <p className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-amber-800">
            Poiché il conto è sempre dal vivo, <b>modificare la scala di default cambia anche le giornate già
            giocate</b>. Se vuoi intervenire su un solo giorno senza toccare gli altri, usa una giornata speciale.
          </p>
        </div>
      )}
    </div>
  );
}

export default function GestionePunti() {
  const [autorizzato, setAutorizzato] = useState(null); // null = in caricamento
  const [scalaDefault, setScalaDefault] = useState([]);
  const [perGiorno, setPerGiorno] = useState({});       // { "YYYY-MM-DD": [punti] }
  const [giorniAttivi, setGiorniAttivi] = useState([0, 1, 2, 3, 4]);
  const [periodoInizio, setPeriodoInizio] = useState("");
  const [periodoFine, setPeriodoFine] = useState("");
  const [savingDefault, setSavingDefault] = useState(false);

  // Form per la scala di giornata (aggiunta o modifica)
  const [form, setForm] = useState(null); // { data, scala } | null
  const [savingGiorno, setSavingGiorno] = useState(false);

  const carica = () => {
    api.get("/classifica/stato").then((r) => setAutorizzato(!!r.data.puoi_gestire_punti)).catch(() => setAutorizzato(false));
    api.get("/impostazioni").then((r) => {
      setScalaDefault(r.data.scala_punti || []);
      setPerGiorno(r.data.scala_per_giorno || {});
      setGiorniAttivi(r.data.giorni_attivi || []);
      setPeriodoInizio(r.data.periodo_inizio || "");
      setPeriodoFine(r.data.periodo_fine || "");
    }).catch(() => {});
  };

  useEffect(() => { carica(); }, []);
  useRefreshOnPull(carica);

  if (autorizzato === null) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  if (!autorizzato) {
    return (
      <div className="max-w-3xl" data-testid="gestione-punti-negato">
        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">Gestione Punti</h1>
        <div className="card-app p-6 mt-4 flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-slate-100 flex items-center justify-center shrink-0">
            <Lock className="h-5 w-5 text-slate-400" />
          </div>
          <div>
            <p className="font-bold text-slate-800">Sezione riservata</p>
            <p className="text-sm text-slate-500 font-semibold mt-0.5">
              La gestione dei punti è accessibile solo all'Amministratore e al Responsabile che ha designato per la classifica.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const salvaDefault = async () => {
    const nums = scalaDefault.map((n) => parseInt(n, 10)).filter((n) => !isNaN(n) && n >= 0);
    if (!nums.length) return toast.error("Aggiungi almeno una posizione alla scala");
    setSavingDefault(true);
    try {
      const { data } = await api.put("/impostazioni/scala-default", { scala: nums });
      setScalaDefault(data.scala_punti);
      toast.success("Scala di default salvata");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingDefault(false); }
  };

  const apriNuovo = () => setForm({ data: toISO(new Date()), scala: [...(scalaDefault.length ? scalaDefault : [15, 10, 7, 5, 3, 2])] });
  const apriModifica = (data) => {
    const weekday = (new Date(data + "T12:00:00").getDay() + 6) % 7;
    const attiva = !!periodoInizio && !!periodoFine && data >= periodoInizio && data <= periodoFine && giorniAttivi.includes(weekday);
    if (!attiva) return toast.error("Le giornate fuori dal periodo o dai giorni attivi sono consultabili ma non modificabili");
    setForm({ data, scala: [...perGiorno[data]] });
  };

  const salvaGiorno = async () => {
    if (!dataAttiva) return toast.error("Puoi salvare una scala solo durante il periodo e nei giorni attivi dell'oratorio");
    const nums = form.scala.map((n) => parseInt(n, 10)).filter((n) => !isNaN(n) && n >= 0);
    if (!nums.length) return toast.error("Aggiungi almeno una posizione alla scala");
    setSavingGiorno(true);
    try {
      const { data } = await api.put("/impostazioni/scala-giorno", { data: form.data, scala: nums });
      setPerGiorno(data.scala_per_giorno || {});
      toast.success(`Scala speciale del ${fmtData(form.data)} salvata`);
      setForm(null);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSavingGiorno(false); }
  };

  const rimuoviGiorno = async (data) => {
    const weekday = (new Date(data + "T12:00:00").getDay() + 6) % 7;
    const attiva = !!periodoInizio && !!periodoFine && data >= periodoInizio && data <= periodoFine && giorniAttivi.includes(weekday);
    if (!attiva) return toast.error("Le giornate fuori dal periodo o dai giorni attivi sono consultabili ma non modificabili");
    try {
      const { data: res } = await api.delete(`/impostazioni/scala-giorno?data=${data}`);
      setPerGiorno(res.scala_per_giorno || {});
      toast.success("Scala speciale rimossa");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const giorni = Object.keys(perGiorno).sort();

  // Validità della data scelta nel form rispetto ai giorni attivi dell'oratorio
  const weekdayApp = form ? (new Date(form.data + "T12:00:00").getDay() + 6) % 7 : null;
  const dataAttiva = form ? !!periodoInizio && !!periodoFine && form.data >= periodoInizio && form.data <= periodoFine && giorniAttivi.includes(weekdayApp) : true;

  return (
    <div className="max-w-3xl" data-testid="gestione-punti-page">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">Gestione Punti</h1>
      <p className="text-sm text-slate-500 font-semibold mb-6">Scala di default e giornate speciali della classifica</p>

      <ComeFunziona />

      {/* Scala di default */}
      <div className="card-app p-4 sm:p-6 mb-4">
        <p className="font-display font-extrabold text-slate-900 mb-1 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-amber-500" /> Scala punti di default
        </p>
        <p className="text-xs text-slate-400 font-semibold mb-4">
          Punti assegnati in base alla posizione finale in un gioco. Vale per ogni gioco che non ha una scala propria.
        </p>
        <ScalaEditor scala={scalaDefault} onChange={setScalaDefault} />
        <button className="btn-primary mt-4" disabled={savingDefault} onClick={salvaDefault} data-testid="scala-default-salva">
          <Save className="h-4 w-4" /> {savingDefault ? "Salvataggio..." : "Salva scala di default"}
        </button>
      </div>

      {/* Scala per giornata */}
      <div className="card-app p-4 sm:p-6 mb-4">
        <div className="flex items-start justify-between gap-3 mb-1">
          <p className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" /> Giornate speciali
          </p>
          {!form && (
            <button className="btn-secondary !py-2" onClick={apriNuovo} data-testid="scala-giorno-nuovo">
              <Plus className="h-4 w-4" /> Aggiungi
            </button>
          )}
        </div>
        <p className="text-xs text-slate-400 font-semibold mb-4">
          Per una data specifica puoi usare una scala diversa. Nelle giornate speciali questa scala <b>sostituisce sia il default sia le scale dei singoli giochi</b>. Modificarla ricalcola solo quella giornata.
        </p>

        {/* Elenco override esistenti */}
        {giorni.length === 0 && !form && (
          <p className="text-sm text-slate-400 font-semibold bg-slate-50 rounded-2xl p-4 text-center">
            Nessuna giornata speciale. La classifica usa ovunque la scala di default.
          </p>
        )}

        <div className="space-y-2.5">
          {giorni.map((d) => (
            <div key={d} className="rounded-2xl border border-slate-100 bg-white p-3.5" data-testid={`scala-giorno-riga-${d}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4 text-violet-500" />
                  {fmtData(d)}
                  <span className="text-xs font-semibold text-slate-400">
                    {GIORNI_SETTIMANA[(new Date(d + "T12:00:00").getDay() + 6) % 7]}
                  </span>
                </p>
                <div className="flex items-center gap-1">
                  <button className="btn-ghost !p-1.5" onClick={() => apriModifica(d)} data-testid={`scala-giorno-modifica-${d}`} aria-label="Modifica">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <ConfirmAction onConfirm={() => rimuoviGiorno(d)} title={`Rimuovere la scala del ${fmtData(d)}?`} description="La giornata tornerà a usare la scala di default (o quella dei singoli giochi)." testId={`scala-giorno-rimuovi-${d}`}>
                    <button className="btn-ghost !p-1.5 text-red-500 hover:bg-red-50" aria-label="Rimuovi">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </ConfirmAction>
                </div>
              </div>
              <ScalaPills scala={perGiorno[d]} />
            </div>
          ))}
        </div>

        {/* Form aggiunta/modifica */}
        {form && (
          <div className="mt-4 rounded-3xl border-2 border-violet-100 bg-violet-50/40 p-4" data-testid="scala-giorno-form">
            <Field label="Giornata" hint="Scegli la data dell'oratorio a cui applicare la scala speciale">
              <input type="date" className="input-app max-w-[200px]" value={form.data}
                onChange={(e) => setForm((f) => ({ ...f, data: e.target.value }))} data-testid="scala-giorno-data" />
            </Field>
            {!dataAttiva && (
              <p className="text-xs font-bold text-amber-600 -mt-2 mb-3">
                Attenzione: questa data non rientra nel periodo o nei giorni attivi dell'oratorio. È consultabile, ma non modificabile.
              </p>
            )}
            <ScalaEditor scala={form.scala} onChange={(s) => setForm((f) => ({ ...f, scala: s }))} />
            <div className="flex flex-wrap gap-2 mt-4">
              <button className="btn-primary" disabled={savingGiorno || !dataAttiva} onClick={salvaGiorno} data-testid="scala-giorno-salva">
                <Save className="h-4 w-4" /> {savingGiorno ? "Salvataggio..." : "Salva giornata"}
              </button>
              <button className="btn-ghost" onClick={() => setForm(null)} data-testid="scala-giorno-annulla">Annulla</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
